import { WEB_ASSETS, settingsPageForHref } from '@tenfour/contracts'
import styles from './PageHeaderIcon.module.css'
import { DestinationTile, type DestinationTone } from './DestinationTile'
import { EXTRA_DESTINATION_ICONS, type ExtraDestinationIconKey } from './extraDestinationIcons'
import { SettingsIcon } from './SettingsIcon'

type PageHeaderIconKey = keyof typeof WEB_ASSETS.icons.pageHeaders
type DestinationIconKey = PageHeaderIconKey | ExtraDestinationIconKey

const PAGE_HEADER_ICONS: Record<string, DestinationIconKey> = {
  '/home': 'home',
  // Needs your attention wears the service bell; the `queue` slug is the
  // older briefcase, which stays with Jobs below.
  '/queue': 'needsYou',
  '/aria': 'aria',
  '/messages': 'messages',
  '/calls': 'callHandling',
  '/conversations': 'messages',
  '/money': 'money',
  '/customers-v2': 'customers',
  '/customers': 'customers',
  '/jobs': 'queue',
  '/history': 'activity',
  '/forms': 'forms',
  '/invoices': 'invoices',
  '/reminders': 'reminders',
  '/calendar': 'calendar',
  '/meetings': 'meetings',
  '/calendar/meetings': 'meetings',
  '/reports': 'reports',
  '/notifications': 'activity',
  '/settings': 'accountSettings',
  '/settings/overview': 'settingsOverview',
  '/settings/invoices': 'invoices',
  '/settings/phone-assistant': 'phoneAssistant',
  '/settings/call-handling': 'callHandling',
  '/settings/services': 'services',
  '/settings/payments': 'paymentMethods',
  '/settings/billing': 'paymentMethods',
  '/settings/preferences': 'preferences',
  '/settings/birthdays': 'birthdayTexts',
  '/settings/reviews': 'reviewFollowups',
  '/settings/subscriptions': 'subscriptions',
  '/settings/team': 'team',
  '/settings/security': 'security',
}

/** Resolve exact routes first, then inherit the owning section for detail URLs. */
export function resolvePageHeaderIconKey(pathname: string): DestinationIconKey | null {
  if (pathname.startsWith('/calendar/meetings/')) return 'meetings'
  const exact = PAGE_HEADER_ICONS[pathname]
  if (exact) return exact
  return PAGE_HEADER_ICONS['/' + pathname.split('/')[1]] ?? null
}

function destinationAssets(key: DestinationIconKey) {
  return key in EXTRA_DESTINATION_ICONS
    ? EXTRA_DESTINATION_ICONS[key as ExtraDestinationIconKey]
    : WEB_ASSETS.icons.pageHeaders[key as PageHeaderIconKey]
}

interface PageHeaderIconProps {
  pathname: string
  className?: string
  /** Active/current art; most destinations fill, while Aria changes to its solid-orange mark. */
  state?: 'idle' | 'active'
  /**
   * Rendered on the settings rail's colour chip. Uses the recoloured chip art:
   * the mark's body carries its group's tone and the inset detail stays cream,
   * which is what lets it sit on a pale tile of that same tone. One asset, both
   * themes, always filled — the tile does not change with theme or route state.
   */
  onTile?: boolean
  /**
   * Rendered as a rail destination tile: the filled light art pigmented into
   * this tone on a tinted 26px tile (DestinationTile). The mark stays filled
   * whether or not the route is current (design system sidebar v6), so `state`
   * only matters for the flat, untoned mark.
   */
  tone?: DestinationTone
}

/**
 * Compact authored destination mark shared by the page header bar, sidebar,
 * and mobile bottom tab bar. Most meanings are clay reliefs; Aria is the exact
 * loader lemniscate. All state/theme assets stay mounted so theme,
 * pointer-down, and route-state swaps are immediate. The empty alt plus
 * aria-hidden wrapper keep the nearby label as the sole accessible name.
 */
export function PageHeaderIcon({
  pathname,
  className,
  state = 'active',
  onTile = false,
  tone,
}: PageHeaderIconProps) {
  const settingsPage = settingsPageForHref(pathname)
  if (settingsPage) return <SettingsIcon page={settingsPage} />
  const key = resolvePageHeaderIconKey(pathname)
  if (!key) return null

  const assets = destinationAssets(key)

  if (tone) {
    // The pigment maps luminance, so it needs ink art. Aria's filled mark is
    // solid brand orange; its ink lemniscate (the idle art) carries the tone.
    const src = key === 'aria' ? assets.idle.light : assets.active.light
    return <DestinationTile src={src} tone={tone} className={className} />
  }

  if (onTile) {
    const chip = WEB_ASSETS.icons.settingsChips[key as keyof typeof WEB_ASSETS.icons.settingsChips]
    return (
      <span
        className={[styles.icon, styles.stateActive, className].filter(Boolean).join(' ')}
        data-icon-state="active"
        aria-hidden="true"
      >
        <img
          className={`${styles.asset} ${styles.activeAsset}`}
          src={chip ?? assets.active.dark}
          alt=""
        />
      </span>
    )
  }

  return (
    <span
      className={[
        styles.icon,
        state === 'active' ? styles.stateActive : styles.stateIdle,
        className,
      ].filter(Boolean).join(' ')}
      data-icon-state={state}
      aria-hidden="true"
    >
      <img
        className={`${styles.asset} ${styles.idleAsset} ${styles.light}`}
        src={assets.idle.light}
        alt=""
      />
      <img
        className={`${styles.asset} ${styles.idleAsset} ${styles.dark}`}
        src={assets.idle.dark}
        alt=""
      />
      <img
        className={`${styles.asset} ${styles.activeAsset} ${styles.light}`}
        src={assets.active.light}
        alt=""
      />
      <img
        className={`${styles.asset} ${styles.activeAsset} ${styles.dark}`}
        src={assets.active.dark}
        alt=""
      />
    </span>
  )
}
